<template>
  <v-dialog width="600" :value="opened" persistent scrollable>
    <input
      type="file"
      style="display: none"
      ref="singlePageInput"
      @change="onSelectFileForSinglePage"
      accept=".pdf"
    />

    <input
      type="file"
      style="display: none"
      ref="multiPageInput"
      @change="onSelectFileForMultiPage"
      accept=".pdf"
    />

    <v-card>
      <v-card-title class="title py-2">
        <v-layout align-start> Manage Target Sheets </v-layout>

        <v-spacer />

        <v-btn
          small
          flat
          fab
          class="white error--text text-capitalize"
          @click="$emit('update:opened', false)"
        >
          <v-icon size="24"> clear </v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="py-3 text-xs-left">
        <v-layout row wrap align-center>
          <v-flex xs3>
            <span class="body-2">
              Single Page
            </span>
          </v-flex>

          <v-flex xs9>
            <v-layout v-if="singlePageTargetSheet" align-center>
              <span class="caption">
                Uploaded:
                {{ singlePageTargetSheet.integration_data.document_name }}
              </span>

              <v-btn
                icon
                flat
                color="error"
                @click="removeTargetSheet('single-page')"
                :loading="removingSinglePage"
              >
                <v-icon small>remove_circle</v-icon>
              </v-btn>
            </v-layout>

            <v-btn
              v-else
              color="primary"
              class="text-none"
              @click="$refs.singlePageInput.click()"
              :loading="uploadingSinglePage"
            >
              <v-icon small class="pr-1">upload</v-icon>
              Upload target sheet
            </v-btn>
          </v-flex>
        </v-layout>

        <v-divider class="my-2" />

        <v-layout row wrap align-center>
          <v-flex xs3>
            <span class="body-2">
              Multi-Page
            </span>
          </v-flex>

          <v-flex xs9>
            <v-layout v-if="multiPageTargetSheet" align-center>
              <span class="caption">
                Uploaded:
                {{ multiPageTargetSheet.integration_data.document_name }}
              </span>

              <v-btn
                icon
                flat
                color="error"
                @click="removeTargetSheet('multi-page')"
                :loading="removingMultiPage"
              >
                <v-icon small>remove_circle</v-icon>
              </v-btn>
            </v-layout>

            <v-btn
              v-else
              color="primary"
              class="text-none"
              @click="$refs.multiPageInput.click()"
              :loading="uploadingMultiPage"
            >
              <v-icon small class="pr-1">upload</v-icon>
              Upload target sheet
            </v-btn>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import API from '@/api';
import * as Sentry from '@sentry/vue';

export default {
  props: {
    opened: {
      type: Boolean,
    },
  },

  data() {
    return {
      uploadingSinglePage: false,
      removingSinglePage: false,

      uploadingMultiPage: false,
      removingMultiPage: false,

      uploadedTargetSheets: [],
    };
  },

  methods: {
    onSelectFileForSinglePage(e) {
      const file = e.target.files[0];

      this.uploadFile(file, 'single-page');
    },

    onSelectFileForMultiPage(e) {
      const file = e.target.files[0];

      this.uploadFile(file, 'multi-page');
    },

    async uploadFile(file, targetSheetType) {
      if (targetSheetType === 'single-page') {
        this.uploadingSinglePage = true;
      } else {
        this.uploadingMultiPage = true;
      }

      try {
        const {
          data: { id, upload_link },
        } = await API.integration.getTemporaryUploadLinkForTargetSheets();

        await API.integration.uploadToDropbox(upload_link, file);

        const response = await API.integration.processTargetSheet({
          id,
          documentType: targetSheetType,
          documentName: file.name,
        });
      } catch (error) {
        console.log(error);
        Sentry.captureException(error);
      } finally {
        await this.getUploadedTargetSheets();

        if (targetSheetType === 'single-page') {
          this.uploadingSinglePage = false;
          this.$refs.singlePageInput.value = null;
        } else {
          this.uploadingMultiPage = false;
          this.$refs.multiPageInput.value = null;
        }
      }
    },

    async removeTargetSheet(targetSheetType) {
      if (targetSheetType === 'single-page') {
        this.removingSinglePage = true;
      } else {
        this.removingMultiPage = true;
      }

      try {
        await API.integration.deleteTargetSheet({
          documentType: targetSheetType,
        });

        await this.getUploadedTargetSheets();
      } catch (error) {
        console.log(error);
        Sentry.captureException(error);
      } finally {
        if (targetSheetType === 'single-page') {
          this.removingSinglePage = false;
        } else {
          this.removingMultiPage = false;
        }
      }
    },

    async getUploadedTargetSheets() {
      const response = await API.integration.getUploadedTargetSheets();

      this.uploadedTargetSheets = response.data;
    },
  },

  computed: {
    singlePageTargetSheet() {
      return this.uploadedTargetSheets.find(
        sheet => sheet.integration_data.document_type === 'single-page',
      );
    },

    multiPageTargetSheet() {
      return this.uploadedTargetSheets.find(
        sheet => sheet.integration_data.document_type === 'multi-page',
      );
    },
  },

  watch: {
    opened(val) {
      if (val) {
        this.getUploadedTargetSheets();
      }
    },
  },

  async created() {
    await this.getUploadedTargetSheets();
  },
};
</script>

<style scoped></style>
